import axios from 'axios';
import bookingModal from '../modal';
import cryptDecrypt from '../../../core/crypt-decrypt';
import checkTime from '../check-time';

declare let booking_object: any;

const modCryptDecrypt = new cryptDecrypt("25a02a871c482b08a36db790703c455c03e8e38d37d9615ba4b98be07f7a6842");

interface BookingData{

    order_uuid: string;
    room_id: number;
    room_name: string;
    game_name: string;
    arena_id: number;
    type_arena: string;
    mode: string;
    game_id: number;
    date_start: string;
    date_end: string;
    players: number;
    price_paid: string;
    price_left: string;
    price_total: string;
    time: string;
    slot_time: string;
    day: string;

}

interface EncryptedData{

    encryptedData: string;
    iv: string;

}

export default class BookingStepOne{

    private readonly apiUrl: string;
    private output: boolean = false;

    constructor(){
        
        this.apiUrl = booking_object.rest_urls.step_one;

    }

    /**
     * Sends form data via a POST request.
     * @param nextButton Selector for the submit button.
     * @param elForm Selector for the form.
     * @returns {Promise<boolean>} Request status.
     */
    async sendData(nextButton: string, elForm: string): Promise<boolean> {

        let spin: HTMLElement | null = null;

        try{    
            
            const btn = document.querySelector(nextButton) as HTMLButtonElement | null;
            const form = document.querySelector(elForm) as HTMLFormElement | null;

            if(!form) throw new Error("Form not found!");

            spin = this.createSpinner();
            btn?.append(spin);

            const formData = this.prepareFormData(form);
            const orderData = this.getLocalStorage();
            formData.append("order_id", orderData ? orderData.order_uuid : '');

            const postData = new URLSearchParams({ fields: new URLSearchParams(formData as any).toString() }).toString();

            const response = await this.sendPostRequest(postData);

            if(response.status){
                
                if(orderData == null){
                    
                    // Start countdown
                    const modChecktime = new checkTime(response.order_data.created_at);
                    modChecktime.startCountDown();

                }
                
                this.storeLocalStorage(response.order_data);
                this.output = true;

            }else{

                this.handleError(response.message);
                this.output = false;

            }

        }catch(error){
            
            // console.error("Error sending data:", error);
            this.output = false;

        }finally{
            
            // Ensures the spinner is always removed
            if(spin) spin.remove();

        }

        return this.output;

    }

    /**
     * Creates a loading spinner icon.
     * @returns {HTMLElement} Spinner element.
     */
    private createSpinner(): HTMLElement{

        const spin = document.createElement('i');
        spin.id = 'bgdrop-spin';
        spin.className = 'fa-solid fa-circle-notch fa-spin ml-1 mt-1';
        return spin;

    }

    /**
     * Prepares form data by adding price values from the UI.
     * @param form The form element.
     * @returns {FormData} Updated form data.
     */
    private prepareFormData(form: HTMLFormElement): FormData {
        
        const formData = new FormData(form);

        const smrPricePaid = document.querySelector('#smr-price-paid') as HTMLSpanElement;
        const smrPriceTotal = document.querySelector('#smr-price-full') as HTMLSpanElement;
        const smrPriceLeft = document.querySelector('#smr-price-left') as HTMLSpanElement;

        formData.append("price_paid", smrPricePaid?.innerHTML || "0");
        formData.append("price_left", smrPriceLeft?.innerHTML || "0");
        formData.append("price_total", smrPriceTotal?.innerHTML || "0");

        return formData;

    }

    /**
     * Sends a POST request to the API.
     * @param data The request data.
     * @returns {Promise<any>} API response.
     */
    private async sendPostRequest(data: string): Promise<any> {
        
        try{

            const response = await axios.post(this.apiUrl, data,{
                validateStatus: status => status === 200,
            });

            return response.data;

        }catch (error: any){
            
            if(error.response && error.response.status === 201){
                
                this.handleError(error.response.data.message);

            }

            throw error;

        }

    }

    /**
     * Handles errors by displaying a message and opening the modal.
     * @param message Error message.
     */
    private handleError(message: string){
        
        document.body.insertAdjacentHTML('beforeend', message);
        bookingModal('errorModal', 'closeErrorModal');

    }

    /**
     * Stores encrypted booking data in Local Storage.
     * @param data Booking data.
     */
    private storeLocalStorage(data: BookingData){

        const encrypted = modCryptDecrypt.encryptObject(data);
        localStorage.setItem('booking-data-order', JSON.stringify(encrypted));

    }

    /**
     * Retrieves and decrypts booking data from Local Storage.
     * @returns {BookingData | null} Decrypted booking data or null if not found.
     */
    getLocalStorage(): BookingData | null {

        const storedData = localStorage.getItem('booking-data-order');
        if (!storedData) return null;

        try{

            const parsedData: EncryptedData = JSON.parse(storedData);
            return modCryptDecrypt.decryptObject<BookingData>(parsedData.encryptedData, parsedData.iv);

        }catch (error){
            
            // console.error("Error decrypting data:", error);
            return null;

        }

    }

}
